import { Pencil, Plus } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  Tabs,
  useToast,
  type Column,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import {
  useDecideScheduleRequest,
  useManagerSchedules,
  useScheduleDoctors,
  useScheduleMutations,
  type ScheduleChangeRequest,
  type ScheduleKind,
  type StaffSchedule,
} from '@/features/schedules/api';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import { useOptionalUrlState, useUrlState } from '@/lib/url-state';
import styles from './ManagerSchedulesPage.module.css';

type ManagerTab = 'schedules' | 'requests';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0] as const;

function minutesToTime(minutes: number) {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function timeToMinutes(value: string) {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function displayDate(value: string) {
  return new Intl.DateTimeFormat('en-MY', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${value}T00:00:00`));
}

function RequestError({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  if (
    isApiError(error) &&
    (error.code === 'SCHEDULE_NOT_FOUND' ||
      error.code === 'SCHEDULE_REQUEST_NOT_FOUND')
  ) {
    return (
      <ErrorState
        error={error}
        title={
          error.code === 'SCHEDULE_NOT_FOUND'
            ? t('schedules.scheduleStale')
            : t('schedules.requestStale')
        }
        body={t('schedules.staleGuidance')}
      />
    );
  }
  return <ErrorState error={error} />;
}

export function ManagerSchedulesPage() {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const { branches } = useBranch();
  const query = useManagerSchedules();
  const doctors = useScheduleDoctors();
  const [tab, setTab] = useUrlState<ManagerTab>('tab', 'schedules', [
    'schedules',
    'requests',
  ]);
  const [drawer, setDrawer] = useOptionalUrlState('drawer');
  const editing: StaffSchedule | 'new' | null =
    drawer === 'new'
      ? 'new'
      : (query.data?.schedules.find((schedule) => schedule.id === drawer) ??
        null);
  const pending =
    query.data?.requests.filter((request) => request.status === 'pending') ??
    [];
  const decided =
    query.data?.requests.filter((request) => request.status !== 'pending') ??
    [];
  const branchName = (id: string) =>
    branches.find((branch) => branch.id === id)?.name ?? id;
  const doctorName = (id: string) =>
    doctors.data?.find((doctor) => doctor.id === id)?.fullName ?? id;

  const columns: Column<StaffSchedule>[] = [
    {
      key: 'doctor',
      header: t('schedules.doctor'),
      render: (schedule) => <strong>{doctorName(schedule.staffUserId)}</strong>,
    },
    {
      key: 'branch',
      header: t('schedules.branch'),
      render: (schedule) => branchName(schedule.branchId),
    },
    {
      key: 'when',
      header: t('schedules.dayOrDate'),
      render: (schedule) =>
        schedule.kind === 'weekly_template'
          ? t(`schedules.weekdays.${schedule.weekday}`)
          : displayDate(schedule.date!),
    },
    {
      key: 'kind',
      header: t('schedules.scheduleType'),
      render: (schedule) => (
        <Badge tone={schedule.kind === 'date_override' ? 'info' : 'neutral'}>
          {t(`schedules.scheduleKinds.${schedule.kind}`)}
        </Badge>
      ),
    },
    {
      key: 'hours',
      header: t('schedules.hours'),
      render: (schedule) =>
        `${minutesToTime(schedule.startMin)}–${minutesToTime(schedule.endMin)}`,
    },
    {
      key: 'actions',
      header: <span className="visually-hidden">{t('schedules.actions')}</span>,
      align: 'right',
      render: (schedule) => (
        <Button
          size="sm"
          icon={<Pencil size={14} />}
          onClick={() => setDrawer(schedule.id)}
        >
          {t('schedules.edit')}
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <div className={styles.stack}>
        <Tabs
          variant="bar"
          label={t('schedules.managerTitle')}
          value={tab}
          onChange={setTab}
          items={[
            { value: 'schedules', label: t('schedules.schedulesTab') },
            {
              value: 'requests',
              label: t('schedules.requestsTab'),
              count: pending.length,
            },
          ]}
        />

        {tab === 'schedules' ? (
          <Card
            title={t('schedules.managerTitle')}
            flush
            actions={
              <Button
                size="sm"
                variant="primary"
                icon={<Plus size={14} />}
                onClick={() => setDrawer('new')}
              >
                {t('schedules.addSchedule')}
              </Button>
            }
          >
            <DataTable
              caption={t('schedules.managerTitle')}
              rows={query.data?.schedules}
              rowKey={(schedule) => schedule.id}
              loading={query.isLoading}
              error={query.error}
              onRetry={() => void query.refetch()}
              empty={
                <EmptyState
                  title={t('schedules.noSchedules')}
                  body={t('schedules.noSchedulesBody')}
                />
              }
              columns={columns}
            />
          </Card>
        ) : (
          <RequestsTab
            loading={query.isLoading}
            error={query.error}
            pending={pending}
            decided={decided}
            canDecide={hasPermission('schedule_requests:decide')}
            branchName={branchName}
            doctorName={doctorName}
            onRetry={() => void query.refetch()}
          />
        )}
      </div>

      {editing && (
        <ScheduleDrawer
          schedule={editing === 'new' ? null : editing}
          doctorOptions={doctors.data ?? []}
          doctorsLoading={doctors.isLoading}
          doctorsError={doctors.error}
          onRetryDoctors={() => void doctors.refetch()}
          onClose={() => setDrawer(null)}
        />
      )}
    </PageContainer>
  );
}

function RequestsTab({
  loading,
  error,
  pending,
  decided,
  canDecide,
  branchName,
  doctorName,
  onRetry,
}: {
  loading: boolean;
  error: unknown;
  pending: ScheduleChangeRequest[];
  decided: ScheduleChangeRequest[];
  canDecide: boolean;
  branchName: (id: string) => string;
  doctorName: (id: string) => string;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const decide = useDecideScheduleRequest();
  const toast = useToast();
  const [deciding, setDeciding] = useState<{
    id: string;
    status: 'approved' | 'declined';
  } | null>(null);

  const decideRequest = (
    request: ScheduleChangeRequest,
    status: 'approved' | 'declined',
  ) => {
    decide.reset();
    setDeciding({ id: request.id, status });
    decide.mutate(
      { id: request.id, status },
      {
        onSuccess: () => {
          toast.show({
            tone: 'success',
            title:
              status === 'approved'
                ? t('schedules.requestApproved')
                : t('schedules.requestDeclined'),
          });
          setDeciding(null);
        },
        onError: () => setDeciding(null),
      },
    );
  };

  const baseColumns: Column<ScheduleChangeRequest>[] = [
    {
      key: 'doctor',
      header: t('schedules.doctor'),
      render: (request) => <strong>{doctorName(request.staffUserId)}</strong>,
    },
    {
      key: 'branch',
      header: t('schedules.branch'),
      render: (request) => branchName(request.branchId),
    },
    {
      key: 'date',
      header: t('schedules.date'),
      render: (request) => displayDate(request.date),
    },
    {
      key: 'kind',
      header: t('schedules.requestKind'),
      render: (request) =>
        t(`schedules.requestKinds.${request.kind}`, {
          defaultValue: request.kind,
        }),
    },
    {
      key: 'details',
      header: t('schedules.details'),
      render: (request) => request.text,
    },
  ];
  const pendingColumns: Column<ScheduleChangeRequest>[] = [
    ...baseColumns,
    {
      key: 'actions',
      header: t('schedules.actions'),
      align: 'right',
      render: (request) =>
        canDecide ? (
          <div className={styles.rowActions}>
            <Button
              size="sm"
              variant="danger"
              loading={
                deciding?.id === request.id && deciding.status === 'declined'
              }
              disabled={decide.isPending}
              onClick={() => decideRequest(request, 'declined')}
            >
              {t('schedules.decline')}
            </Button>
            <Button
              size="sm"
              variant="primary"
              loading={
                deciding?.id === request.id && deciding.status === 'approved'
              }
              disabled={decide.isPending}
              onClick={() => decideRequest(request, 'approved')}
            >
              {t('schedules.approve')}
            </Button>
          </div>
        ) : null,
    },
  ];
  const decidedColumns: Column<ScheduleChangeRequest>[] = [
    ...baseColumns,
    {
      key: 'status',
      header: t('schedules.status'),
      render: (request) => (
        <Badge tone={request.status === 'approved' ? 'success' : 'danger'}>
          {t(`schedules.requestStatuses.${request.status}`)}
        </Badge>
      ),
    },
  ];

  if (error) return <ErrorState error={error} onRetry={onRetry} />;

  return (
    <div className={styles.stack}>
      <RequestError error={decide.error} />
      <Card title={t('schedules.pendingRequests')} flush>
        <DataTable
          caption={t('schedules.pendingRequests')}
          rows={pending}
          rowKey={(request) => request.id}
          loading={loading}
          empty={<EmptyState title={t('schedules.noPendingRequests')} />}
          columns={pendingColumns}
        />
      </Card>
      <Card title={t('schedules.decidedRequests')} flush>
        <DataTable
          caption={t('schedules.decidedRequests')}
          rows={decided}
          rowKey={(request) => request.id}
          loading={loading}
          empty={<EmptyState title={t('schedules.noDecidedRequests')} />}
          columns={decidedColumns}
        />
      </Card>
    </div>
  );
}

function ScheduleDrawer({
  schedule,
  doctorOptions,
  doctorsLoading,
  doctorsError,
  onRetryDoctors,
  onClose,
}: {
  schedule: StaffSchedule | null;
  doctorOptions: { id: string; fullName: string }[];
  doctorsLoading: boolean;
  doctorsError: unknown;
  onRetryDoctors: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { branches } = useBranch();
  const actions = useScheduleMutations();
  const toast = useToast();
  const [staffUserId, setStaffUserId] = useState(schedule?.staffUserId ?? '');
  const [branchId, setBranchId] = useState(
    schedule?.branchId ?? branches[0]?.id ?? '',
  );
  const [kind, setKind] = useState<ScheduleKind>(
    schedule?.kind ?? 'weekly_template',
  );
  const [weekday, setWeekday] = useState(String(schedule?.weekday ?? 1));
  const [date, setDate] = useState(schedule?.date ?? '');
  const [start, setStart] = useState(
    schedule ? minutesToTime(schedule.startMin) : '08:00',
  );
  const [end, setEnd] = useState(
    schedule ? minutesToTime(schedule.endMin) : '17:00',
  );
  const [attempted, setAttempted] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const mutationError =
    actions.create.error ?? actions.update.error ?? actions.remove.error;
  const saving = actions.create.isPending || actions.update.isPending;

  const resetErrors = () => {
    actions.create.reset();
    actions.update.reset();
    actions.remove.reset();
  };

  const close = () => {
    resetErrors();
    onClose();
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    resetErrors();
    const startMin = timeToMinutes(start);
    const endMin = timeToMinutes(end);
    if (
      !staffUserId ||
      !branchId ||
      (kind === 'weekly_template' ? !weekday : !date) ||
      startMin === null ||
      endMin === null ||
      startMin >= endMin
    ) {
      return;
    }
    const input = {
      staffUserId,
      branchId,
      kind,
      weekday: kind === 'weekly_template' ? Number(weekday) : null,
      date: kind === 'date_override' ? date : null,
      startMin,
      endMin,
    };
    const mutation = schedule
      ? actions.update.mutateAsync({ id: schedule.id, ...input })
      : actions.create.mutateAsync(input);
    void mutation
      .then(() => {
        toast.show({
          tone: 'success',
          title: schedule
            ? t('schedules.scheduleUpdated')
            : t('schedules.scheduleCreated'),
        });
        close();
      })
      .catch(() => undefined);
  };

  const remove = () => {
    if (!schedule) return;
    resetErrors();
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    void actions.remove
      .mutateAsync(schedule.id)
      .then(() => {
        toast.show({ tone: 'success', title: t('schedules.scheduleDeleted') });
        close();
      })
      .catch(() => undefined);
  };

  const timeError =
    attempted &&
    (timeToMinutes(start) === null ||
      timeToMinutes(end) === null ||
      timeToMinutes(start)! >= timeToMinutes(end)!)
      ? t('schedules.invalidHours')
      : undefined;
  const doctorChoices = doctorOptions.some(
    (doctor) => doctor.id === schedule?.staffUserId,
  )
    ? doctorOptions
    : schedule
      ? [
          { id: schedule.staffUserId, fullName: schedule.staffUserId },
          ...doctorOptions,
        ]
      : doctorOptions;

  return (
    <Dialog
      open
      onClose={close}
      dismissible={!saving && !actions.remove.isPending}
      title={
        schedule ? t('schedules.editSchedule') : t('schedules.addSchedule')
      }
      footer={
        <>
          {schedule && (
            <Button
              variant="danger"
              loading={actions.remove.isPending}
              onClick={remove}
            >
              {confirmDelete
                ? t('schedules.confirmDelete')
                : t('schedules.delete')}
            </Button>
          )}
          <Button onClick={close}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            type="submit"
            form="schedule-form"
            loading={saving}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <form id="schedule-form" className={styles.form} onSubmit={submit}>
        {doctorsError && !schedule ? (
          <ErrorState error={doctorsError} onRetry={onRetryDoctors} />
        ) : (
          <Field
            label={t('schedules.doctor')}
            error={
              attempted && !staffUserId
                ? t('schedules.doctorRequired')
                : undefined
            }
          >
            <Select
              value={staffUserId}
              disabled={doctorsLoading}
              placeholder={t('schedules.chooseDoctor')}
              options={doctorChoices.map((doctor) => ({
                value: doctor.id,
                label: doctor.fullName,
              }))}
              onChange={(event) => setStaffUserId(event.target.value)}
            />
          </Field>
        )}
        <Field label={t('schedules.branch')}>
          <Select
            value={branchId}
            options={branches.map((branch) => ({
              value: branch.id,
              label: branch.name,
            }))}
            onChange={(event) => setBranchId(event.target.value)}
          />
        </Field>
        <Field label={t('schedules.scheduleType')}>
          <Select
            value={kind}
            options={(['weekly_template', 'date_override'] as const).map(
              (value) => ({
                value,
                label: t(`schedules.scheduleKinds.${value}`),
              }),
            )}
            onChange={(event) => setKind(event.target.value as ScheduleKind)}
          />
        </Field>
        {kind === 'weekly_template' ? (
          <Field label={t('schedules.weekday')}>
            <Select
              value={weekday}
              options={WEEKDAYS.map((value) => ({
                value: String(value),
                label: t(`schedules.weekdays.${value}`),
              }))}
              onChange={(event) => setWeekday(event.target.value)}
            />
          </Field>
        ) : (
          <Field
            label={t('schedules.date')}
            error={attempted && !date ? t('schedules.dateRequired') : undefined}
          >
            <Input
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
          </Field>
        )}
        <div className={styles.timeFields}>
          <Field label={t('schedules.startTime')} error={timeError}>
            <Input
              type="time"
              value={start}
              onChange={(event) => setStart(event.target.value)}
            />
          </Field>
          <Field label={t('schedules.endTime')}>
            <Input
              type="time"
              value={end}
              onChange={(event) => setEnd(event.target.value)}
            />
          </Field>
        </div>
        <RequestError error={mutationError} />
      </form>
    </Dialog>
  );
}
